import { useState, useEffect, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSort, faSortUp, faSortDown } from '@fortawesome/free-solid-svg-icons';
import { airlinerService } from '../../../services/airlinerService';

const AircraftDetails = () => {
  const navigate = useNavigate();
  const { id, airlinerId } = useParams();
  
  // Check if being used by admin
  const isAdminMode = window.location.pathname.includes('/admin/');
  const adminAirlinerId = airlinerId; // In admin mode, airlinerId is in URL

  const [aircraft, setAircraft] = useState(null);
  const [aircraftImage, setAircraftImage] = useState(null);
  const [aircraftImages, setAircraftImages] = useState([]); // All images
  const [selectedImageIndex, setSelectedImageIndex] = useState(0); // Currently displayed image
  const [flights, setFlights] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingFlights, setLoadingFlights] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [recordsPerPage, setRecordsPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [visibleColumns] = useState({
    regNo: true,
    flightNo: true,
    depAirport: true,
    etd: true,
    arrAirport: true,
    eta: true,
    elPrice: true,
    seatPrice: true,
    seatTotal: true,
    seatAvailable: true,
    flightTime: true,
    status: true
  });
  const [pagination, setPagination] = useState({
    totalItems: 0,
    totalPages: 1,
    currentPage: 1,
    itemsPerPage: 10
  });
  const [sortConfig, setSortConfig] = useState({ key: 'etd', direction: 'desc' });

  useEffect(() => {
    const fetchAircraftDetails = async () => {
      if (!id) return;

      try {
        setLoading(true);
        setError(null);

        // Fetch only aircraft and images data
        const response = await airlinerService.getAircraftById(id);

        if (response.success && response.data) {
          setAircraft(response.data.aircraft || response.data);

          // Set aircraft images if available
          if (response.data.images && response.data.images.length > 0) {
            setAircraftImages(response.data.images);
            const firstImage = response.data.images[0];
            setAircraftImage(firstImage.presigned_url || null);
            setSelectedImageIndex(0);
          } else {
            setAircraftImages([]);
            setAircraftImage(null);
          }
        } else {
          setError('Failed to load aircraft details');
        }
      } catch (err) {
        console.error('Error fetching aircraft details:', err);
        setError(err.message || 'Failed to load aircraft details');
      } finally {
        setLoading(false);
      }
    };

    fetchAircraftDetails();
  }, [id]);

  // Separate useEffect for fetching flights with search, pagination, and sorting
  useEffect(() => {
    const fetchFlights = async () => {
      if (!id) return;

      try {
        setLoadingFlights(true);

        // Map sortConfig to API parameters
        const sortByMap = {
          'index': 'id',
          'regNo': 'registration_number',
          'flightNo': 'flight_number',
          'depAirport': 'departure_airport_id',
          'etd': 'departure_date_time',
          'arrAirport': 'arrival_airport_id',
          'eta': 'arrival_date_time',
          'elPrice': 'block_price',
          'seatPrice': 'seat_price',
          'seatTotal': 'seat_available',
          'seatAvailable': 'seat_available',
          'flightTime': 'flight_time',
          'status': 'status'
        };

        const sortBy = sortConfig.key ? (sortByMap[sortConfig.key] || 'departure_date_time') : 'departure_date_time';
        const sortOrder = sortConfig.direction.toUpperCase();

        // Call the separate routes API with search, pagination, and sorting
        const response = await airlinerService.getAircraftRoutes(id, {
          page: currentPage,
          limit: recordsPerPage,
          search: searchTerm,
          sortBy,
          sortOrder
        });

        if (response.success && response.data) {
          setFlights(response.data || []);

          if (response.pagination) {
            setPagination({
              totalItems: response.pagination.totalRecords || 0,
              totalPages: response.pagination.totalPages || 1,
              currentPage: response.pagination.currentPage || 1,
              itemsPerPage: response.pagination.perPage || recordsPerPage
            });
          }
        }
      } catch (err) {
        console.error('Error fetching flights:', err);
        // Don't set error state for flights, just log it
      } finally {
        setLoadingFlights(false);
      }
    };

    fetchFlights();
  }, [id, currentPage, recordsPerPage, searchTerm, sortConfig]);

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1);
  };

  const handleRecordsPerPageChange = (e) => {
    setRecordsPerPage(parseInt(e.target.value));
    setCurrentPage(1);
  };

  const handlePreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const handleNextPage = () => {
    if (currentPage < pagination.totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  // Sorting handlers
  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
    setCurrentPage(1); // Reset to first page when sorting changes
  };

  const renderSortIcon = (key) => {
    if (sortConfig.key !== key) {
      return <FontAwesomeIcon icon={faSort} className="ml-2 text-gray-400 opacity-50" />;
    }
    return (
      <FontAwesomeIcon
        icon={sortConfig.direction === 'asc' ? faSortUp : faSortDown}
        className="ml-2 text-black"
      />
    );
  };

  // Process flights to add sequential ID based on server pagination
  const processedFlights = useMemo(() => {
    const offset = (pagination.currentPage - 1) * pagination.itemsPerPage;
    return flights.map((flight, index) => ({
      ...flight,
      seq_id: offset + index + 1
    }));
  }, [flights, pagination]);

  // Use data directly from API (server-side handles sorting, filtering, and pagination)
  const totalRecords = pagination.totalItems || 0;
  const startIndex = (pagination.currentPage - 1) * pagination.itemsPerPage;
  const endIndex = Math.min(startIndex + processedFlights.length, totalRecords);
  const currentData = processedFlights;

  const handleBack = () => {
    if (isAdminMode && adminAirlinerId) {
      navigate(`/admin/airliners/${adminAirlinerId}/aircrafts`);
    } else {
      navigate('/airliner/aircraft');
    }
  };

  const handleAddFlight = () => {
    if (isAdminMode && adminAirlinerId) {
      navigate(`/admin/aircraft/route/${id}`);
    } else {
      navigate(`/airliner/aircraft/${id}/add-flight`);
    }
  };

  return (
    <div className="p-0 bg-jet-gray-100 min-h-screen w-full relative font-['Bahnschrift_Light','Segoe_UI',Tahoma,Geneva,Verdana,sans-serif]">
      {/* Header */}
      <div className="py-5 px-[30px] flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 bg-transparent">
        <h1 className="m-0 text-[18px] font-bold mb-[7px] font-['Bahnschrift_Light'] text-[#333]">Aircraft Details</h1>
        <div className="flex gap-2 w-full sm:w-auto">
          <button
            type="button"
            className="text-black h-9 py-2 px-4 text-sm bg-gradient-to-br from-[#ffeb00] to-[#ffd200] border-none rounded cursor-pointer font-medium transition-all duration-200 inline-flex items-center gap-2 hover:from-[#ffd200] hover:to-[#ffb300]"
            onClick={handleAddFlight}
          >
            <i className="fa fa-plus"></i> Add Flight
          </button>
          <button
            type="button"
            className="text-black h-9 py-2 px-4 text-sm bg-gradient-to-br from-[#ffeb00] to-[#ffd200] border-none rounded cursor-pointer font-medium transition-all duration-200 inline-flex items-center gap-2 hover:from-[#ffd200] hover:to-[#ffb300]"
            onClick={handleBack}
          >
            <i className="fa fa-arrow-left"></i> Back
          </button>
        </div>
      </div>

      <div className="py-0 px-[30px] pb-[30px]">
        {/* Aircraft Details Card */}
        <section className="bg-white p-[20px] mb-5 rounded-lg shadow-[0_1px_3px_rgba(0,0,0,0.05)]">
          {loading ? (
            <div className="text-center p-5 text-[#999]">Loading aircraft details...</div>
          ) : error ? (
            <div className="text-center p-5 text-red-500">Error: {error}</div>
          ) : aircraft ? (
            <>
              {/* Top Section: Main Image and Details Side by Side */}
              <div className="flex flex-col lg:flex-row gap-[30px] mb-5">
                {/* Left: Main Image */}
                <div className="flex-shrink-0">
                  <img
                    src={aircraftImage || '/no_aircraft.png'}
                    alt="Aircraft"
                    className="w-full lg:w-[180px] h-[120px] rounded border border-[#ddd] object-cover"
                    onError={(e) => { e.target.src = '/no_aircraft.png'; }}
                  />
                </div>

                {/* Right: Details Table */}
                <div className="flex-1">
                  <div className="border-b border-[#e0e0e0]">
                    <div className="relative block px-[15px] py-[10px] -mb-[1px] bg-white border border-[#ddd] border-l-0 border-r-0 flex justify-between items-center">
                      <label className="text-[13px] font-normal text-[#666]">Model</label>
                      <span className="text-[14px] text-[#333] font-medium">
                        {aircraft.aircraftmodel ?
                          `${aircraft.aircraftmodel.manaufacturer}/${aircraft.aircraftmodel.model}/${aircraft.aircraftmodel.type_designator}`
                          : 'N/A'}
                      </span>
                    </div>
                    <div className="relative block px-[15px] py-[10px] -mb-[1px] bg-white border border-[#ddd] border-l-0 border-r-0 flex justify-between items-center">
                      <label className="text-[13px] font-normal text-[#666]">Reg No.</label>
                      <span className="text-[14px] text-[#333] font-medium">{aircraft.registration_number || 'N/A'}</span>
                    </div>
                    <div className="relative block px-[15px] py-[10px] -mb-[1px] bg-white border border-[#ddd] border-l-0 border-r-0 flex justify-between items-center">
                      <label className="text-[13px] font-normal text-[#666]">Seat(s)</label>
                      <span className="text-[14px] text-[#333] font-medium">{aircraft.seating_capacity || 'N/A'}</span>
                    </div>
                    <div className="relative block px-[15px] py-[10px] -mb-[1px] bg-white border border-[#ddd] border-l-0 border-r-0 flex justify-between items-center">
                      <label className="text-[13px] font-normal text-[#666]">Endurance</label>
                      <span className="text-[14px] text-[#333] font-medium">{aircraft.endurance || 'N/A'}</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Bottom Section: Thumbnail Gallery */}
              {aircraftImages.length > 0 && (
                <div className="flex gap-2.5 flex-wrap">
                  {aircraftImages.map((img, index) => (
                    <div
                      key={img.id}
                      onClick={() => {
                        setAircraftImage(img.presigned_url);
                        setSelectedImageIndex(index);
                      }}
                      className={`w-[160px] h-[100px] rounded border-2 cursor-pointer overflow-hidden transition-all ${
                        selectedImageIndex === index 
                          ? 'border-[#ffd200] shadow-lg' 
                          : 'border-[#ddd] hover:border-[#aaa]'
                      }`}
                    >
                      <img
                        src={img.presigned_url || '/no_aircraft.png'}
                        alt={`Aircraft ${index + 1}`}
                        className="w-full h-full object-cover"
                        onError={(e) => { e.target.src = '/no_aircraft.png'; }}
                      />
                    </div>
                  ))}
                </div>
              )}
            </>
          ) : (
            <div className="text-center p-5 text-[#999]">No aircraft data available</div>
          )}
        </section>

        {/* Flight(s) Listed Table */}
        <div className="bg-white">
          <div className="py-[15px] px-[20px] bg-transparent mb-[15px]">
            <h3 className="text-base font-bold text-[#333] m-0 mb-[15px]">Flight(s) Listed</h3>
            <div className="flex items-center justify-between w-full gap-4">
              <div className="flex items-center gap-2 text-[13px] text-[#555]">
                <span>Display</span>
                <select
                  value={recordsPerPage}
                  onChange={handleRecordsPerPageChange}
                  className="w-[75px] h-[34px] py-1.5 px-3 text-[13px] leading-[1.42857143] text-black bg-white border border-[#ccc] rounded-[5px] shadow-none font-semibold inline-block align-middle transition-[border-color] duration-150 focus:outline-none focus:border-[#ccc] focus:shadow-none"
                >
                  <option value={10}>10</option>
                  <option value={25}>25</option>
                  <option value={50}>50</option>
                  <option value={100}>100</option>
                </select>
                <span>Records</span>
              </div>
              <div className="flex items-center gap-2.5">
                <label className="text-[13px] text-[#555] font-normal">Search</label>
                <input
                  type="text"
                  placeholder=""
                  value={searchTerm}
                  onChange={handleSearch}
                  className="w-[200px] h-[34px] py-1.5 px-3 text-[13px] leading-[1.42857143] text-[#4e4c4c] bg-white border border-[#ccc] rounded-[5px] shadow-none align-middle appearance-none box-border transition-[border-color,box-shadow] duration-150 font-normal focus:outline-none focus:border-[#ccc] focus:shadow-none placeholder:text-[#999] placeholder:font-normal"
                />
                <div className="inline-flex">
                  <button
                    onClick={handlePreviousPage}
                    className="py-[5px] px-4 border border-[#ddd] bg-white rounded-tl-md rounded-bl-md rounded-tr-none rounded-br-none border-r-0 cursor-pointer text-base text-[#aaa] h-[34px] min-w-[100px] font-light inline-block align-middle appearance-none box-border transition-all duration-200 leading-[1.5] text-center whitespace-nowrap select-none shadow-[0_1px_3px_rgba(0,0,0,0.05)] hover:bg-[#f9f9f9] hover:border-[#ccc] hover:text-[#888] focus:outline-none focus:border-[#ddd] focus:shadow-[0_1px_3px_rgba(0,0,0,0.05)]"
                  >
                    Previous
                  </button>
                  <button
                    onClick={handleNextPage}
                    className="py-[5px] px-4 border border-[#ddd] bg-white rounded-tr-md rounded-br-md rounded-tl-none rounded-bl-none cursor-pointer text-base text-[#aaa] h-[34px] min-w-[100px] font-light inline-block align-middle appearance-none box-border transition-all duration-200 leading-[1.5] text-center whitespace-nowrap select-none shadow-[0_1px_3px_rgba(0,0,0,0.05)] hover:bg-[#f9f9f9] hover:border-[#ccc] hover:text-[#888] focus:outline-none focus:border-[#ddd] focus:shadow-[0_1px_3px_rgba(0,0,0,0.05)]"
                  >
                    Next
                  </button>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white mx-0 mt-0 mb-[15px] rounded-none shadow-none border border-[#ddd] overflow-x-auto">
            <table className="w-full border-collapse min-w-[800px]">
              <thead className="bg-[#fafafa] border-b-0">
                <tr>
                  <th onClick={() => handleSort('index')} className={`py-1.5 px-2.5 pr-[30px] text-left font-semibold text-[12px] align-middle border-[#f4f4f4] cursor-pointer hover:bg-[#f0f0f0] transition-colors ${sortConfig.key === 'index' ? 'text-black bg-[#f0f0f0]' : 'text-gray-600'}`}>
                    <span className="inline-flex items-center gap-1"># {renderSortIcon('index')}</span>
                  </th>
                  {visibleColumns.regNo && (
                    <th onClick={() => handleSort('regNo')} className={`py-1.5 px-2.5 pr-[30px] text-left font-semibold text-[12px] align-middle border-[#f4f4f4] cursor-pointer hover:bg-[#f0f0f0] transition-colors ${sortConfig.key === 'regNo' ? 'text-black bg-[#f0f0f0]' : 'text-gray-600'}`}>
                      <span className="inline-flex items-center gap-1">Reg No. {renderSortIcon('regNo')}</span>
                    </th>
                  )}
                  {visibleColumns.flightNo && (
                    <th onClick={() => handleSort('flightNo')} className={`py-1.5 px-2.5 pr-[30px] text-left font-semibold text-[12px] align-middle border-[#f4f4f4] cursor-pointer hover:bg-[#f0f0f0] transition-colors ${sortConfig.key === 'flightNo' ? 'text-black bg-[#f0f0f0]' : 'text-gray-600'}`}>
                      <span className="inline-flex items-center gap-1">Flight No. {renderSortIcon('flightNo')}</span>
                    </th>
                  )}
                  {visibleColumns.depAirport && (
                    <th onClick={() => handleSort('depAirport')} className={`py-1.5 px-2.5 pr-[30px] text-left font-semibold text-[12px] align-middle border-[#f4f4f4] cursor-pointer hover:bg-[#f0f0f0] transition-colors ${sortConfig.key === 'depAirport' ? 'text-black bg-[#f0f0f0]' : 'text-gray-600'}`}>
                      <span className="inline-flex items-center gap-1">Dep. Airport {renderSortIcon('depAirport')}</span>
                    </th>
                  )}
                  {visibleColumns.etd && (
                    <th onClick={() => handleSort('etd')} className={`py-1.5 px-2.5 pr-[30px] text-left font-semibold text-[12px] align-middle border-[#f4f4f4] cursor-pointer hover:bg-[#f0f0f0] transition-colors ${sortConfig.key === 'etd' ? 'text-black bg-[#f0f0f0]' : 'text-gray-600'}`}>
                      <span className="inline-flex items-center gap-1">ETD {renderSortIcon('etd')}</span>
                    </th>
                  )}
                  {visibleColumns.arrAirport && (
                    <th onClick={() => handleSort('arrAirport')} className={`py-1.5 px-2.5 pr-[30px] text-left font-semibold text-[12px] align-middle border-[#f4f4f4] cursor-pointer hover:bg-[#f0f0f0] transition-colors ${sortConfig.key === 'arrAirport' ? 'text-black bg-[#f0f0f0]' : 'text-gray-600'}`}>
                      <span className="inline-flex items-center gap-1">Arr. Airport {renderSortIcon('arrAirport')}</span>
                    </th>
                  )}
                  {visibleColumns.eta && (
                    <th onClick={() => handleSort('eta')} className={`py-1.5 px-2.5 pr-[30px] text-left font-semibold text-[12px] align-middle border-[#f4f4f4] cursor-pointer hover:bg-[#f0f0f0] transition-colors ${sortConfig.key === 'eta' ? 'text-black bg-[#f0f0f0]' : 'text-gray-600'}`}>
                      <span className="inline-flex items-center gap-1">ETA {renderSortIcon('eta')}</span>
                    </th>
                  )}
                  {visibleColumns.elPrice && (
                    <th onClick={() => handleSort('elPrice')} className={`py-1.5 px-2.5 pr-[30px] text-left font-semibold text-[12px] align-middle border-[#f4f4f4] cursor-pointer hover:bg-[#f0f0f0] transition-colors ${sortConfig.key === 'elPrice' ? 'text-black bg-[#f0f0f0]' : 'text-gray-600'}`}>
                      <span className="inline-flex items-center gap-1">EL Price {renderSortIcon('elPrice')}</span>
                    </th>
                  )}
                  {visibleColumns.seatPrice && (
                    <th onClick={() => handleSort('seatPrice')} className={`py-1.5 px-2.5 pr-[30px] text-left font-semibold text-[12px] align-middle border-[#f4f4f4] cursor-pointer hover:bg-[#f0f0f0] transition-colors ${sortConfig.key === 'seatPrice' ? 'text-black bg-[#f0f0f0]' : 'text-gray-600'}`}>
                      <span className="inline-flex items-center gap-1">Seat Price {renderSortIcon('seatPrice')}</span>
                    </th>
                  )}
                  {visibleColumns.seatTotal && (
                    <th onClick={() => handleSort('seatTotal')} className={`py-1.5 px-2.5 pr-[30px] text-left font-semibold text-[12px] align-middle border-[#f4f4f4] cursor-pointer hover:bg-[#f0f0f0] transition-colors ${sortConfig.key === 'seatTotal' ? 'text-black bg-[#f0f0f0]' : 'text-gray-600'}`}>
                      <span className="inline-flex items-center gap-1">Total Seat {renderSortIcon('seatTotal')}</span>
                    </th>
                  )}
                  {visibleColumns.seatAvailable && (
                    <th onClick={() => handleSort('seatAvailable')} className={`py-1.5 px-2.5 pr-[30px] text-left font-semibold text-[12px] align-middle border-[#f4f4f4] cursor-pointer hover:bg-[#f0f0f0] transition-colors ${sortConfig.key === 'seatAvailable' ? 'text-black bg-[#f0f0f0]' : 'text-gray-600'}`}>
                      <span className="inline-flex items-center gap-1">Available Seat {renderSortIcon('seatAvailable')}</span>
                    </th>
                  )}
                  {visibleColumns.flightTime && (
                    <th onClick={() => handleSort('flightTime')} className={`py-1.5 px-2.5 pr-[30px] text-left font-semibold text-[12px] align-middle border-[#f4f4f4] cursor-pointer hover:bg-[#f0f0f0] transition-colors ${sortConfig.key === 'flightTime' ? 'text-black bg-[#f0f0f0]' : 'text-gray-600'}`}>
                      <span className="inline-flex items-center gap-1">Flight Time {renderSortIcon('flightTime')}</span>
                    </th>
                  )}
                  {visibleColumns.status && (
                    <th onClick={() => handleSort('status')} className={`py-1.5 px-2.5 pr-[30px] text-left font-semibold text-[12px] align-middle border-[#f4f4f4] cursor-pointer hover:bg-[#f0f0f0] transition-colors ${sortConfig.key === 'status' ? 'text-black bg-[#f0f0f0]' : 'text-gray-600'}`}>
                      <span className="inline-flex items-center gap-1">Status {renderSortIcon('status')}</span>
                    </th>
                  )}
                </tr>
              </thead>
              <tbody>
                {loadingFlights ? (
                  <tr>
                    <td colSpan={Object.values(visibleColumns).filter(Boolean).length + 1} className="text-center p-5 text-[#999] text-[13px]">
                      Loading flights...
                    </td>
                  </tr>
                ) : currentData.length === 0 ? (
                  <tr>
                    <td colSpan={Object.values(visibleColumns).filter(Boolean).length + 1} className="text-center p-5 text-[#999] text-[13px]">
                      No data available in table
                    </td>
                  </tr>
                ) : (
                  currentData.map((flight, index) => (
                    <tr key={flight.id} className="hover:bg-[#fafafa]">
                      <td className="py-3 px-[15px] border-b border-[#ddd] text-[13px] text-[#555] border-r border-[#ddd] last:border-r-0">
                        {flight.seq_id}
                      </td>
                      {visibleColumns.regNo && (
                        <td className="py-3 px-[15px] border-b border-[#ddd] text-[13px] text-[#555] border-r border-[#ddd] last:border-r-0">
                          {flight.registration_number || 'N/A'}
                        </td>
                      )}
                      {visibleColumns.flightNo && (
                        <td className="py-3 px-[15px] border-b border-[#ddd] text-[13px] text-[#555] border-r border-[#ddd] last:border-r-0">
                          {flight.flight_number || 'N/A'}
                        </td>
                      )}
                      {visibleColumns.depAirport && (
                        <td className="py-3 px-[15px] border-b border-[#ddd] text-[13px] text-[#555] border-r border-[#ddd] last:border-r-0">
                          {flight.dep_airport || 'N/A'}
                        </td>
                      )}
                      {visibleColumns.etd && (
                        <td className="py-3 px-[15px] border-b border-[#ddd] text-[13px] text-[#555] border-r border-[#ddd] last:border-r-0">
                          {flight.departure_date_time ? new Date(flight.departure_date_time).toLocaleString() : 'N/A'}
                        </td>
                      )}
                      {visibleColumns.arrAirport && (
                        <td className="py-3 px-[15px] border-b border-[#ddd] text-[13px] text-[#555] border-r border-[#ddd] last:border-r-0">
                          {flight.arr_airport || 'N/A'}
                        </td>
                      )}
                      {visibleColumns.eta && (
                        <td className="py-3 px-[15px] border-b border-[#ddd] text-[13px] text-[#555] border-r border-[#ddd] last:border-r-0">
                          {flight.arrival_date_time ? new Date(flight.arrival_date_time).toLocaleString() : 'N/A'}
                        </td>
                      )}
                      {visibleColumns.elPrice && (
                        <td className="py-3 px-[15px] border-b border-[#ddd] text-[13px] text-[#555] border-r border-[#ddd] last:border-r-0">
                          {flight.currency || 'USD'} {flight.block_price || '0.00'}
                        </td>
                      )}
                      {visibleColumns.seatPrice && (
                        <td className="py-3 px-[15px] border-b border-[#ddd] text-[13px] text-[#555] border-r border-[#ddd] last:border-r-0">
                          {flight.currency || 'USD'} {flight.seat_price || '0.00'}
                        </td>
                      )}
                      {visibleColumns.seatTotal && (
                        <td className="py-3 px-[15px] border-b border-[#ddd] text-[13px] text-[#555] border-r border-[#ddd] last:border-r-0">
                          {flight.seat_total || 0}
                        </td>
                      )}
                      {visibleColumns.seatAvailable && (
                        <td className="py-3 px-[15px] border-b border-[#ddd] text-[13px] text-[#555] border-r border-[#ddd] last:border-r-0">
                          {flight.seat_available || 0}
                        </td>
                      )}
                      {visibleColumns.flightTime && (
                        <td className="py-3 px-[15px] border-b border-[#ddd] text-[13px] text-[#555] border-r border-[#ddd] last:border-r-0">
                          {flight.flight_time || 'N/A'}
                        </td>
                      )}
                      {visibleColumns.status && (
                        <td className="py-3 px-[15px] border-b border-[#ddd] text-[13px] text-[#555] border-r border-[#ddd] last:border-r-0 text-center">
                          {flight.status === 'published' ? (
                            flight.not_operate_flight === 1 ? (
                              flight.booking_count === 0 ? (
                                <span className="inline-block py-1 px-2 text-xs font-semibold text-white bg-gray-500 rounded">DNO</span>
                              ) : (
                                <span className="inline-block py-1 px-2 text-xs font-semibold text-white bg-blue-500 rounded">Completed</span>
                              )
                            ) : (
                              <span className="inline-block py-1 px-2 text-xs font-semibold text-white bg-green-500 rounded">Active</span>
                            )
                          ) : flight.status === 'canceled' || flight.status === 'cancelled' ? (
                            <span className="inline-block py-1 px-2 text-xs font-semibold text-white bg-red-500 rounded">Cancelled</span>
                          ) : flight.status === 'draft' || flight.status === 'saved' ? (
                            <span className="inline-block py-1 px-2 text-xs font-semibold text-white bg-orange-500 rounded">Saved</span>
                          ) : flight.deleted_at ? (
                            <span className="inline-block py-1 px-2 text-xs font-semibold text-white bg-blue-500 rounded">Deleted</span>
                          ) : (
                            <span className="inline-block py-1 px-2 text-xs font-semibold text-white bg-gray-500 rounded">{flight.status || 'N/A'}</span>
                          )}
                        </td>
                      )}
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          <div className="flex justify-between items-center mx-0 my-0 py-[15px] px-[20px] bg-transparent flex-col md:flex-row gap-2.5 md:gap-0">
            <div className="text-[13px] text-[#555] text-center md:text-left">
              Showing {totalRecords === 0 ? 0 : startIndex + 1} to {endIndex} of {totalRecords} entries
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AircraftDetails;
